import React from "react";

// material-ui
import { makeStyles } from "@material-ui/core/styles";

// jazasoft
import { MuiTable } from "@jazasoft/mui-table";
import { Button, BackButton, PageFooter } from "jazasoft";

// icons
import HistoryIcon from "@material-ui/icons/History";
// import PrintIcon from "@material-ui/icons/Print";
import ViewIcon from "@material-ui/icons/RemoveRedEye";

// local
import { cbChecklistItemSort } from "../../../utils/helpers";
import { Role } from "../../../utils/types";

import useCuChecklistView from "./useCuChecklistView.js";
import HistoryDialog from "../../../components/HistoryDialog";
import TechSheetDialog from "./TechSheetDialog";
import hasRole from "../../../utils/hasRole";
import hasPrivilege from "../../../utils/hasPrivilege";

// const colors = ["#EDF5FD", "#FFEFE3", "#E7E2EF", "#C8F3E6", "#FCE9EE"];
const colors = ["#D1E3FF", "#FFEFE3", "#E6F9E6", "#FCE9EE"];

// Basic Comparator for Merchant
const basicComparator = (a, b) => {
  if (a.itemNo === b.itemNo) {
    return a.styleName ? -1 : 1;
  } else {
    return a.itemNo - b.itemNo;
  }
};

const useStyles = makeStyles(theme => ({
  content: {
    margin: theme.spacing(3)
  },
  container: {
    margin: theme.spacing(3)
  },
  pageHeader: {
    "&:last-child": {
      paddingBottom: "1.5em"
    }
  },
  pageFooter: {
    paddingBottom: "1.5em",
    paddingTop: "15px",
    "&>button": {
      marginLeft: "1.5em"
    }
  },
  nowrapTable: {
    whiteSpace: "pre"
  },
  headerCell: {
    fontWeight: "bold",
    fontFamily: "Roboto, sans-serif",
    padding: "1em 2em",
    "&:last-child": {
      padding: "1em 2em",
      fontWeight: "bold",
      fontFamily: "Roboto, sans-serif"
    }
  },
  rowCell: {
    padding: "1em 2em",
    "&:last-child": {
      padding: "1em 2em"
    }
  }
}));

const CuChecklistView = props => {
  const { roles = [], hasAccess } = props;
  const classes = useStyles();

  const {
    cuChecklist,
    columns,
    rows,
    dialogState,
    busy,
    onSubmit,
    onSelectActionClick,
    onInlineActionClick,
    onDownloadTechSheet,
    onUploadTechSheets,
    onDeleteTechSheet,
    setDialogState
  } = useCuChecklistView(props);

  const hasSelectPermission = hasRole(roles, [Role.SUPER_USER, Role.ADMIN_USER, Role.MERCHANT, Role.CUT_PLANNER]);
  const selectable = hasSelectPermission ? row => row?.state === "New" && row?.styleName : false;
  let inlineActions = [];
  if (hasRole(roles, [Role.SUPER_USER, Role.ADMIN_USER, Role.MERCHANT, Role.CUT_PLANNER])) {
    inlineActions.push({ name: "edit", tooltip: "Edit Row" });
  }
  if (hasPrivilege(roles, hasAccess, "est_log", "read")) {
    inlineActions.push({ name: "history", tooltip: "History", icon: <HistoryIcon /> });
  }
  const selectActions = hasRole(roles, [Role.SUPER_USER, Role.ADMIN_USER])
    ? [
        { name: "delete", tooltip: `Delete Rows` },
        { name: "add", tooltip: `Create ${cuChecklist?.checklistType}` }
      ]
    : hasRole(roles, [Role.MERCHANT])
    ? [{ name: "delete", tooltip: `Delete Rows` }]
    : hasRole(roles, [Role.CUT_PLANNER])
    ? [{ name: "add", tooltip: `Create ${cuChecklist?.checklistType}` }]
    : [];

  const historyUrl = dialogState.cuChecklistItemId
    ? `cuChecklists/${props.id}/cuChecklistItems/${dialogState.cuChecklistItemId}/logs`
    : `cuChecklists/${props.id}/logs`;

  const comparator = hasRole(roles, [Role.MERCHANT]) ? basicComparator : cbChecklistItemSort;

  return (
    <div>
      <HistoryDialog open={(dialogState.active && dialogState.action === "history") || false} url={historyUrl} onClose={() => setDialogState({})} />

      <TechSheetDialog
        dialogState={dialogState}
        techSheets={cuChecklist?.techSheets}
        busy={busy}
        hasAccess={hasRole(roles, [Role.ADMIN_USER, Role.SUPER_USER, Role.MERCHANT])}
        onDownloadTechSheet={onDownloadTechSheet}
        onDeleteTechSheet={onDeleteTechSheet}
        onUploadTechSheets={onUploadTechSheets}
        setDialogState={setDialogState}
      />

      <div className={classes.content}>
        <MuiTable
          title="CU Checklist Items"
          columns={columns}
          rows={rows}
          pageable={true}
          pageSize={25}
          searchable={true}
          searchKeys={["poRef", "styleName", "fit"]}
          editable={true}
          idKey="itemNo"
          selectable={selectable}
          selectAll={false}
          selectActions={selectActions}
          inlineActions={inlineActions}
          chipOptions={{ color: "secondary" }}
          actionPlacement="left"
          variant="excel"
          disabledElement="field"
          fontSize={15}
          onSubmit={onSubmit}
          onSelectActionClick={onSelectActionClick}
          onInlineActionClick={onInlineActionClick}
          comparator={comparator}
          rowStyle={({ row }) => ({
            backgroundColor: hasRole(roles, [Role.MERCHANT]) ? "none" : colors[row?.colorIdx % 4]
          })}
          hasRowsChanged={rows => `${rows.length}-${rows.map(e => e.modifiedDate).join("-")}`}
        />
      </div>

      <PageFooter className={classes.pageFooter}>
        <BackButton variant="contained" />
        {hasPrivilege(roles, hasAccess, "est_log", "read") && (
          <Button label="History" variant="contained" onClick={() => setDialogState({ active: true, action: "history" })}>
            <HistoryIcon />
          </Button>
        )}
        <Button label="Tech sheets" variant="contained" onClick={() => setDialogState({ active: true, action: "techSheets" })}>
          <ViewIcon />
        </Button>
      </PageFooter>
    </div>
  );
};

export default CuChecklistView;
